import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCalendar, FiMapPin, FiDollarSign } = FiIcons;

function Projects() {
  const [filter, setFilter] = useState('all');

  const projects = [
    {
      id: 1,
      title: 'Modern Family Residence',
      category: 'residential',
      location: 'Downtown District',
      budget: '$850,000',
      duration: '8 months',
      image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'A contemporary 4-bedroom family home featuring sustainable materials and smart home technology.'
    },
    {
      id: 2,
      title: 'Corporate Office Complex',
      category: 'commercial',
      location: 'Business Park',
      budget: '$2.5M',
      duration: '14 months',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'A state-of-the-art office building with modern amenities and energy-efficient systems.'
    },
    {
      id: 3,
      title: 'Historic Building Renovation',
      category: 'renovation',
      location: 'Heritage District',
      budget: '$1.2M',
      duration: '10 months',
      image: 'https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Careful restoration of a 1920s building while preserving its historic character.'
    },
    {
      id: 4,
      title: 'Luxury Condominium',
      category: 'residential',
      location: 'Waterfront',
      budget: '$1.8M',
      duration: '12 months',
      image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'High-end residential complex with panoramic city views and premium finishes.'
    },
    {
      id: 5,
      title: 'Retail Shopping Center',
      category: 'commercial',
      location: 'Suburban Plaza',
      budget: '$3.2M',
      duration: '16 months',
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Modern shopping center with diverse retail spaces and customer amenities.'
    },
    {
      id: 6,
      title: 'Kitchen & Bath Remodel',
      category: 'renovation',
      location: 'Residential Area',
      budget: '$125,000',
      duration: '3 months',
      image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Complete transformation of kitchen and bathroom spaces with modern fixtures.'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Projects' },
    { id: 'residential', name: 'Residential' },
    { id: 'commercial', name: 'Commercial' },
    { id: 'renovation', name: 'Renovation' }
  ];

  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(project => project.category === filter);

  return (
    <>
      <Helmet>
        <title>Construction Projects Portfolio | BuildCorp Completed Works</title>
        <meta name="description" content="Explore BuildCorp's portfolio of completed construction projects including residential homes, commercial buildings, and renovation projects. See our quality work." />
        <meta name="keywords" content="construction portfolio, completed projects, residential construction examples, commercial building projects, renovation gallery" />
      </Helmet>

      <div className="pt-20">
        {/* Hero Section */}
        <section className="bg-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-secondary mb-6">
                Our <span className="text-primary">Projects</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Explore our portfolio of successful construction projects that showcase our commitment to quality and innovation
              </p>
            </motion.div>
          </div>
        </section>

        {/* Filter Tabs */}
        <section className="py-8 bg-white sticky top-0 z-40 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setFilter(category.id)}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                    filter === category.id
                      ? 'bg-primary text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-64 object-cover transform hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute top-4 right-4 bg-primary text-white px-3 py-1 rounded-full text-sm font-medium capitalize">
                      {project.category}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-secondary mb-3">{project.title}</h3>
                    <p className="text-gray-600 mb-4">{project.description}</p>
                    <div className="space-y-2">
                      <div className="flex items-center text-sm text-gray-500">
                        <SafeIcon icon={FiMapPin} className="w-4 h-4 mr-2 text-primary" />
                        {project.location}
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <SafeIcon icon={FiDollarSign} className="w-4 h-4 mr-2 text-primary" />
                        {project.budget}
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2 text-primary" />
                        {project.duration}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-primary py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold text-white mb-4">
                Ready to Join Our Success Stories?
              </h2>
              <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
                Let us help you bring your construction vision to life with the same quality and dedication
              </p>
              <button className="bg-white text-primary px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors duration-200">
                Start Your Project
              </button>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}

export default Projects;